import { useState, useEffect } from "react";
import { X, CheckCircle, XCircle, RefreshCw, ChevronDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function apiFetch(path, token, opts = {}) {
  return fetch(`${API}/api${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
}

// ── Error-type badge ───────────────────────────────────────────────────────

const ERROR_TYPE_STYLES = {
  Foundational:       { bg: "#fee2e2", text: "#991b1b" },
  Conceptual:         { bg: "#fed7aa", text: "#9a3412" },
  Procedural:         { bg: "#ede9fe", text: "#5b21b6" },
  Careless:           { bg: "#fef9c3", text: "#854d0e" },
  Overgeneralisation: { bg: "#ccfbf1", text: "#134e4a" },
  Notation:           { bg: "#dbeafe", text: "#1e40af" },
  Mixed:              { bg: "#f3f4f6", text: "#374151" },
};

function ErrorTypeBadge({ type }) {
  if (!type) return null;
  const style = ERROR_TYPE_STYLES[type] || { bg: "#f3f4f6", text: "#374151" };
  return (
    <span
      className="inline-block px-2 py-0.5 rounded text-xs font-semibold ml-2"
      style={{ background: style.bg, color: style.text }}
    >
      {type}
    </span>
  );
}

function DifficultyBadge({ difficulty }) {
  const map = {
    L1_EASY:        { label: "L1 Easy",   bg: "#d1fae5", text: "#065f46" },
    L2_MEDIUM_PROC: { label: "L2 Medium", bg: "#dbeafe", text: "#1e40af" },
    L3_MEDIUM_DEPTH:{ label: "L3 Depth",  bg: "#ede9fe", text: "#5b21b6" },
    L4_HARD_PROC:   { label: "L4 Hard",   bg: "#fee2e2", text: "#991b1b" },
    L5_HARD_DEPTH:  { label: "L5 Expert", bg: "#fce7f3", text: "#9d174d" },
  };
  const cfg = map[difficulty] || { label: difficulty || "?", bg: "#f3f4f6", text: "#374151" };
  return (
    <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold" style={{ background: cfg.bg, color: cfg.text }}>
      {cfg.label}
    </span>
  );
}

// ── Single question card ───────────────────────────────────────────────────

function QuestionCard({ q, index, approved, onToggle }) {
  const [showExpl, setShowExpl] = useState(false);

  return (
    <div
      className="rounded-xl border mb-4 overflow-hidden transition-all"
      style={{
        borderColor: approved ? "#6ee7b7" : "#e5e7eb",
        opacity: approved ? 1 : 0.5,
      }}
      data-testid={`question-card-${index}`}
    >
      {/* Card header */}
      <div
        className="flex items-center justify-between px-5 py-3"
        style={{ background: approved ? "#f0fdf4" : "#f9fafb" }}
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-bold text-gray-500">Q{index + 1}</span>
          <DifficultyBadge difficulty={q.difficulty} />
        </div>
        <button
          onClick={onToggle}
          className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
          style={
            approved
              ? { background: "#d1fae5", color: "#065f46" }
              : { background: "#fee2e2", color: "#991b1b" }
          }
          data-testid={`approve-toggle-${index}`}
        >
          {approved ? (
            <><CheckCircle size={13} /> Approved</>
          ) : (
            <><XCircle size={13} /> Rejected</>
          )}
        </button>
      </div>

      {/* Question text */}
      <div className="px-5 pt-4 pb-3">
        <p className="text-sm font-semibold text-gray-900 leading-snug">{q.question_text}</p>
      </div>

      {/* Options — dict format: {"A":"...", "B":"...", "C":"...", "D":"..."} */}
      <div className="px-5 pb-4 space-y-2">
        {Object.entries(q.options || {}).sort().map(([letter, opt]) => {
          const label = q.option_labels?.[letter];
          const isCorrect = letter === q.correct_answer;
          return (
            <div
              key={letter}
              className="flex items-start gap-3 p-3 rounded-lg"
              style={{
                background: isCorrect ? "#f0fdf4" : "#f9fafb",
                border: `1px solid ${isCorrect ? "#6ee7b7" : "#e5e7eb"}`,
              }}
            >
              <span
                className="flex-shrink-0 w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center mt-0.5"
                style={{
                  background: isCorrect ? "#10b981" : "#e5e7eb",
                  color: isCorrect ? "white" : "#6b7280",
                }}
              >
                {letter}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center flex-wrap gap-1">
                  <span className="text-sm text-gray-800">{opt}</span>
                  {isCorrect ? (
                    <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">
                      Correct
                    </span>
                  ) : (
                    label?.error_type && <ErrorTypeBadge type={label.error_type} />
                  )}
                </div>
                {label?.note && (
                  <p className="text-xs text-gray-400 italic mt-0.5 leading-tight">{label.note}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Explanation toggle */}
      <div className="px-5 pb-4">
        <button
          onClick={() => setShowExpl((v) => !v)}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600"
        >
          <ChevronDown size={13} style={{ transform: showExpl ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s" }} />
          {showExpl ? "Hide" : "Show"} explanation
        </button>
        {showExpl && (
          <p className="text-xs text-gray-600 mt-2 leading-relaxed bg-gray-50 p-3 rounded-lg border border-gray-200">
            {q.explanation}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Main GenerateModal component ───────────────────────────────────────────

export default function GenerateModal({ row, token, onClose, onStored }) {
  const [stage, setStage] = useState("generating"); // generating | preview | storing | error
  const [questions, setQuestions] = useState([]);
  const [approved, setApproved] = useState({});
  const [errorMsg, setErrorMsg] = useState("");
  const [budgetError, setBudgetError] = useState(null); // {current_cost, max_budget, required_topup}
  const [storeErrors, setStoreErrors] = useState([]);

  // Compute how many Cold Pool questions to generate: target − current_count, capped at 10
  const coldCount = Math.max(1, Math.min(10, (row.expected_cold || 0) - (row.cold_pool || 0)));

  // Start generation on mount
  useEffect(() => {
    doGenerate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doGenerate = async () => {
    setStage("generating");
    setErrorMsg("");
    setBudgetError(null);
    try {
      const res = await apiFetch("/admin/cr/generate-cold-pool", token, {
        method: "POST",
        body: JSON.stringify({
          grade: row.grade,
          subject: row.subject,
          topic: row.topic,
          sub_topic: row.sub_topic,
          count: coldCount,
        }),
      });
      let data = {};
      try { data = await res.json(); } catch (_) {}
      if (res.status === 402 && data.detail?.error_type === "budget_exceeded") {
        setBudgetError(data.detail);
        setStage("error");
        return;
      }
      if (!res.ok) throw new Error(data.detail || "Generation failed");
      const qs = data.questions || [];
      setQuestions(qs);
      // Default all to approved
      const init = {};
      qs.forEach((_, i) => { init[i] = true; });
      setApproved(init);
      setStage("preview");
    } catch (err) {
      setErrorMsg(err.message || "Generation failed");
      setStage("error");
    }
  };

  const toggleApprove = (i) => {
    setApproved((prev) => ({ ...prev, [i]: !prev[i] }));
  };

  const approveAll = () => {
    const all = {};
    questions.forEach((_, i) => { all[i] = true; });
    setApproved(all);
  };

  const rejectAll = () => {
    const none = {};
    questions.forEach((_, i) => { none[i] = false; });
    setApproved(none);
  };

  const approvedCount = Object.values(approved).filter(Boolean).length;
  const approvedQuestions = questions.filter((_, i) => approved[i]);

  const doStore = async () => {
    if (approvedCount === 0) return;
    setStage("storing");
    try {
      const res = await apiFetch("/admin/cr/store-cold-pool", token, {
        method: "POST",
        body: JSON.stringify({
          questions: approvedQuestions,
          grade: row.grade,
          subject: row.subject,
          sub_topic: row.sub_topic,
        }),
      });
      let data = {};
      try { data = await res.json(); } catch (_) {}
      if (!res.ok) throw new Error(data.detail || "Store failed");
      if (data.errors && data.errors.length > 0) {
        setStoreErrors(data.errors);
        setStage("preview"); // stay on preview so user sees errors
        return;
      }
      onStored(data.stored);
      onClose();
    } catch (err) {
      setErrorMsg(err.message || "Store failed");
      setStage("error");
    }
  };

  // ── Overlay wrapper ──────────────────────────────────────────────────────

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.7)" }}
      data-testid="generate-modal"
    >
      {/* Loading */}
      {stage === "generating" && (
        <div className="bg-white rounded-2xl p-10 max-w-sm w-full text-center shadow-2xl">
          <div
            className="w-10 h-10 rounded-full border-2 border-blue-600 border-t-transparent mx-auto mb-5"
            style={{ animation: "spin 0.8s linear infinite" }}
          />
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <h3 className="font-bold text-gray-900 mb-2">Generating questions via Claude...</h3>
          <p className="text-sm text-gray-500 mb-3">{coldCount} NCERT-scoped Cold Pool MCQ{coldCount !== 1 ? "s" : ""} for:</p>
          <p className="text-sm font-semibold text-blue-700 bg-blue-50 px-4 py-2 rounded-lg">
            {row.sub_topic}
          </p>
          <p className="text-xs text-gray-400 mt-4">{row.grade} · {row.subject}</p>
        </div>
      )}

      {/* Storing */}
      {stage === "storing" && (
        <div className="bg-white rounded-2xl p-10 max-w-sm w-full text-center shadow-2xl">
          <div
            className="w-10 h-10 rounded-full border-2 border-emerald-600 border-t-transparent mx-auto mb-5"
            style={{ animation: "spin 0.8s linear infinite" }}
          />
          <h3 className="font-bold text-gray-900 mb-1">Saving {approvedCount} question{approvedCount !== 1 ? "s" : ""}...</h3>
          <p className="text-sm text-gray-400">Writing to fuel_questions</p>
        </div>
      )}

      {/* Error */}
      {stage === "error" && (
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-2xl">
          {budgetError ? (
            <>
              <div className="flex items-start gap-3 mb-5">
                <div className="w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center"
                  style={{ background: "#fef3c7" }}>
                  <span style={{ fontSize: 20 }}>💳</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">LLM Budget Exhausted</h3>
                  <p className="text-sm text-amber-700 mt-1">{budgetError.message}</p>
                </div>
              </div>
              <div className="rounded-xl p-4 mb-5 space-y-2 text-sm"
                style={{ background: "#fffbeb", border: "1px solid #fde68a" }}>
                <div className="flex justify-between">
                  <span className="text-gray-500">Current spend</span>
                  <span className="font-bold text-red-600">
                    ${budgetError.current_cost != null ? budgetError.current_cost.toFixed(4) : "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Budget limit</span>
                  <span className="font-semibold text-gray-700">
                    ${budgetError.max_budget != null ? budgetError.max_budget.toFixed(4) : "—"}
                  </span>
                </div>
                <div className="border-t pt-2 flex justify-between" style={{ borderColor: "#fde68a" }}>
                  <span className="font-semibold text-gray-700">Recommended top-up</span>
                  <span className="font-bold text-emerald-700">
                    +${budgetError.required_topup != null ? budgetError.required_topup.toFixed(2) : "—"}
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mb-5 leading-relaxed">
                Go to <strong>Profile → Universal Key → Add Balance</strong> (or enable auto top-up), then retry.
              </p>
            </>
          ) : (
            <div className="flex items-start gap-3 mb-5">
              <div className="w-10 h-10 flex-shrink-0 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle size={20} className="text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Generation failed</h3>
                <p className="text-sm text-red-600 mt-1">{errorMsg}</p>
              </div>
            </div>
          )}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={doGenerate}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white"
              style={{ background: "#2563eb" }}
              data-testid="retry-generate-btn"
            >
              <RefreshCw size={14} />
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Preview */}
      {stage === "preview" && (
        <div
          className="bg-white rounded-2xl shadow-2xl flex flex-col"
          style={{ width: "100%", maxWidth: 720, maxHeight: "90vh" }}
          data-testid="generate-preview"
        >
          {/* Modal header */}
          <div
            className="flex items-center justify-between px-6 py-4 flex-shrink-0"
            style={{ borderBottom: "1px solid #e5e7eb" }}
          >
            <div>
              <h2 className="font-bold text-gray-900">Cold Pool Preview</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {row.sub_topic} · {row.grade} · {row.subject}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
              <X size={18} />
            </button>
          </div>

          {/* Sub-header: stats + bulk actions */}
          <div
            className="flex items-center justify-between px-6 py-3 flex-shrink-0"
            style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}
          >
            <div className="flex items-center gap-4">
              <span className="text-sm font-semibold text-gray-700">
                {questions.length} generated
              </span>
              <span
                className="text-sm font-bold"
                style={{ color: approvedCount > 0 ? "#059669" : "#6b7280" }}
              >
                {approvedCount} approved
              </span>
              {storeErrors.length > 0 && (
                <span className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                  {storeErrors.length} validation error{storeErrors.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={rejectAll}
                className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 font-medium"
                data-testid="reject-all-btn"
              >
                Reject All
              </button>
              <button
                onClick={approveAll}
                className="text-xs px-3 py-1.5 rounded-lg font-semibold"
                style={{ background: "#d1fae5", color: "#065f46" }}
                data-testid="approve-all-btn"
              >
                Approve All
              </button>
            </div>
          </div>

          {/* Scrollable questions */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {storeErrors.length > 0 && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-red-700 mb-1">Validation errors (questions not saved):</p>
                <ul className="text-xs text-red-600 space-y-0.5">
                  {storeErrors.map((e, i) => <li key={i}>• {e}</li>)}
                </ul>
              </div>
            )}
            {questions.map((q, i) => (
              <QuestionCard
                key={i}
                q={q}
                index={i}
                approved={!!approved[i]}
                onToggle={() => toggleApprove(i)}
              />
            ))}
            {questions.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-8">No questions generated.</p>
            )}
          </div>

          {/* Footer */}
          <div
            className="flex items-center justify-between px-6 py-4 flex-shrink-0"
            style={{ borderTop: "1px solid #e5e7eb" }}
          >
            <button
              onClick={onClose}
              className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={doStore}
              disabled={approvedCount === 0}
              className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors"
              style={{
                background: approvedCount > 0 ? "#059669" : "#d1d5db",
                cursor: approvedCount > 0 ? "pointer" : "not-allowed",
              }}
              data-testid="save-questions-btn"
            >
              <CheckCircle size={15} />
              Save {approvedCount} Question{approvedCount !== 1 ? "s" : ""}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
